import {useAccessToken} from "../../services/auth"
import React from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import classNames from "classnames"
import {useQueryState} from "use-location-state"
import Callbacks from "./Callbacks"
import {AppConfig, getAppConfig} from "../../resources/whiteLabel"
import Authentication from "./Authentication"
import OutreachSettings from "../OrgSettings/OutreachSettings"
import PaymentProcessing from "./PaymentProcessing"
import Capital from "./Capital"
import BillPaySettings from "../OrgSettings/BillPaySettings"
import SamlSettings from "../OrgSettings/SamlSettings"

type ManagedSolutionsSettingsTabs =
	| "Authentication"
	| "Callbacks"
	| "Outreach"
	| "Payment Processing"
	| "SAML"
	| "Capital"
	| "Bill Pay Settings"

function TabContent({
	currentTab,
	appConfig,
	refresh,
}: {
	currentTab: ManagedSolutionsSettingsTabs
	appConfig: AppConfig
	refresh: () => void
}) {
	if (currentTab == "Authentication") {
		return <Authentication appConfig={appConfig} refresh={refresh} />
	} else if (currentTab == "Callbacks") {
		return <Callbacks appConfig={appConfig} refresh={refresh} />
	} else if (currentTab == "Outreach") {
		return <OutreachSettings refresh={refresh} />
	} else if (currentTab == "Payment Processing") {
		return <PaymentProcessing refresh={refresh} />
	} else if (currentTab == "SAML") {
		return <SamlSettings refresh={refresh} />
	} else if (currentTab == "Capital") {
		return <Capital refresh={refresh} />
	} else if (currentTab == "Bill Pay Settings") {
		return <BillPaySettings refresh={refresh} />
	}

	return null
}

function ManagedSolutionsSettingsInternal({
	appConfig,
	refresh,
	currentTab,
	setCurrentTab,
}: {
	appConfig: AppConfig
	refresh: () => void
	currentTab: ManagedSolutionsSettingsTabs
	setCurrentTab: (tab: ManagedSolutionsSettingsTabs) => void
}) {
	return (
		<>
			<TitleBar title={"Settings"} className={"title-bar-no-border"} />
			<nav className="managed-solutions-settings tabs-container">
				<div className="tabs">
					<ul>
						<li
							className={classNames(currentTab == "Authentication" && "is-active")}
							onClick={() => setCurrentTab("Authentication")}
						>
							<a>Authentication</a>
						</li>
						<li
							className={classNames(currentTab == "Callbacks" && "is-active")}
							onClick={() => setCurrentTab("Callbacks")}
						>
							<a>Callbacks</a>
						</li>
						<li
							className={classNames(currentTab == "Outreach" && "is-active")}
							onClick={() => setCurrentTab("Outreach")}
						>
							<a>Outreach</a>
						</li>
						<li
							className={classNames(currentTab == "Payment Processing" && "is-active")}
							onClick={() => setCurrentTab("Payment Processing")}
						>
							<a>Payment Processing</a>
						</li>
						<li className={classNames(currentTab == "SAML" && "is-active")} onClick={() => setCurrentTab("SAML")}>
							<a>SAML</a>
						</li>
						<li className={classNames(currentTab == "Capital" && "is-active")} onClick={() => setCurrentTab("Capital")}>
							<a>Capital</a>
						</li>
						<li
							className={classNames(currentTab == "Bill Pay Settings" && "is-active")}
							onClick={() => setCurrentTab("Bill Pay Settings")}
						>
							<a>Bill Pay Settings</a>
						</li>
					</ul>
				</div>
			</nav>

			<MainSection>
				<TabContent currentTab={currentTab} refresh={refresh} appConfig={appConfig} />
			</MainSection>
		</>
	)
}

export default function ManagedSolutionsSettingsPage() {
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const [currentTab, setCurrentTab] = useQueryState<ManagedSolutionsSettingsTabs>("tab", "Authentication")
	const appConfigAsync = useAsyncResult(() => getAppConfig(accessToken), [refreshToken])

	return (
		<AsyncResultComponent asyncResult={appConfigAsync}>
			{({value: appConfig}) => {
				return (
					<ManagedSolutionsSettingsInternal
						appConfig={appConfig}
						refresh={refresh}
						currentTab={currentTab}
						setCurrentTab={(t) => {
							setCurrentTab(t)
							refresh()
						}}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
